import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";

import {
  faArrowUpRightFromSquare,
  faCheck,
  faChevronDown,
  faCircleNotch,
  faExpand,
  faWarning,
} from "@fortawesome/pro-regular-svg-icons";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { m } from "motion/react";
import { twJoin, twMerge } from "tailwind-merge";
import { v4 as uuidv4 } from "uuid";

import { DecisionEnvironment } from "src/api/types";
import {
  DurationPill,
  TokensPill,
} from "src/base-components/agents/MetricPills";
import { TOOL_ICON_CONFIG } from "src/base-components/agents/ToolCard";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { Divider } from "src/base-components/Divider";
import { FileCard } from "src/base-components/FileCard";
import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import { Pill } from "src/base-components/Pill";
import { Tabs } from "src/base-components/Tabs";
import type {
  ToolCallContentBlock,
  ToolMessage,
} from "src/clients/agent-management";
import { ToolType } from "src/clients/agent-management";
import { Callout } from "src/design-system/Callout";
import { Icon, IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { getUrlToHistoricDecisionTrace } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ToolCallStatus = "running" | "paused" | "success" | "error";

export type ToolCallWithResult = {
  toolCall: ToolCallContentBlock;
  toolResult: ToolMessage | null;
};

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

export const formatToolOutput = (content: unknown): string => {
  if (typeof content === "string") {
    try {
      const parsed = JSON.parse(content);
      return typeof parsed === "object"
        ? JSON.stringify(parsed, null, 2)
        : content;
    } catch {
      return content;
    }
  }
  return typeof content === "object"
    ? JSON.stringify(content, null, 2)
    : String(content);
};

export const computeToolCallStatus = (
  toolResult: ToolMessage | null,
  sessionSubstatus: string | null,
): ToolCallStatus | null => {
  if (toolResult) {
    return toolResult.status === "error" ? "error" : "success";
  }
  if (sessionSubstatus === "paused") return "paused";
  if (sessionSubstatus) return "running";
  return null;
};

type FlowToolArtifact = {
  flow_id?: string;
  flow_version_id?: string;
  decision_id?: string;
  environment?: string;
};

export const getFlowToolDecisionUrl = (
  toolCall: ToolCallContentBlock,
  toolResult: ToolMessage | null,
  orgId: string,
  wsId: string,
): string | null => {
  if (toolCall.metadata.type !== ToolType.FLOW) return null;

  const artifact = toolResult?.artifact as FlowToolArtifact | null | undefined;
  if (
    !artifact?.flow_id ||
    !artifact.flow_version_id ||
    !artifact.decision_id
  ) {
    return null;
  }

  return getUrlToHistoricDecisionTrace({
    orgId,
    wsId,
    flowId: artifact.flow_id,
    versionId: artifact.flow_version_id,
    decisionId: artifact.decision_id,
    decisionEnv:
      artifact.environment === "sandbox"
        ? DecisionEnvironment.SANDBOX
        : DecisionEnvironment.LIVE,
  });
};

// ---------------------------------------------------------------------------
// AccordionDisclosure
// ---------------------------------------------------------------------------

export const AccordionDisclosure: React.FC<{
  title: React.ReactNode;
  icon?: React.ReactNode;
  rightSlot?: React.ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  erroredBorder?: boolean;
  dataLoc?: string;
}> = ({
  title,
  icon,
  rightSlot,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  children,
  erroredBorder = false,
  dataLoc,
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  // Headless UI's Disclosure manages its own internal open state. In controlled
  // mode we drive rendering from external state, so Disclosure's internal state
  // can diverge — we correct aria-expanded directly on the button before paint.
  const buttonRef = useRef<HTMLButtonElement>(null);
  useLayoutEffect(() => {
    if (isControlled) {
      buttonRef.current?.setAttribute("aria-expanded", String(isOpen));
    }
  });

  const handleToggle = () => {
    if (isControlled) {
      onOpenChange?.(!controlledOpen);
    } else {
      setInternalOpen((prev) => !prev);
    }
  };

  return (
    <Disclosure as="div" defaultOpen={isOpen}>
      <div
        className={twMerge(
          "rounded-lg border border-gray-200 bg-white",
          erroredBorder && "border-orange-400",
        )}
        data-loc={dataLoc}
      >
        <DisclosureButton
          ref={buttonRef}
          className="flex w-full items-center gap-2 p-3"
          onClick={handleToggle}
        >
          <div
            className={twJoin(
              "flex h-4 w-4 shrink-0 items-center justify-center transition-transform duration-200",
              isOpen ? "rotate-0" : "-rotate-90",
            )}
          >
            <Icon icon={faChevronDown} size="2xs" />
          </div>
          {icon}
          <span className="min-w-0 flex-1 truncate text-left font-inter-medium-12px text-gray-800">
            {title}
          </span>
          {rightSlot}
        </DisclosureButton>
        <DisclosurePanel as={Fragment} static>
          <m.div
            animate={
              isOpen
                ? { height: "auto", opacity: 1 }
                : { height: 0, opacity: 0 }
            }
            initial={false}
            style={{ overflow: "hidden" }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            {children}
          </m.div>
        </DisclosurePanel>
      </div>
    </Disclosure>
  );
};

// ---------------------------------------------------------------------------
// ToolTypeIcon
// ---------------------------------------------------------------------------

export const ToolTypeIcon: React.FC<{ type: ToolType }> = ({ type }) => {
  const config = TOOL_ICON_CONFIG[type];
  if (!config) return null;
  return (
    <div
      className={twJoin(
        "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded",
        config.bgColor,
      )}
    >
      <Icon variant={config.iconVariant} icon={config.icon} size="2xs" />
    </div>
  );
};

// ---------------------------------------------------------------------------
// ToolCallStatusPill
// ---------------------------------------------------------------------------

export const ToolCallStatusPill: React.FC<{ status: ToolCallStatus }> = ({
  status,
}) => {
  if (status === "success") {
    return (
      <Pill size="sm" variant="green">
        <Pill.Icon icon={faCheck} />
        <Pill.Text>Successful</Pill.Text>
      </Pill>
    );
  }
  if (status === "error") {
    return (
      <Pill size="sm" variant="orange">
        <Pill.Icon icon={faWarning} />
        <Pill.Text>Failed</Pill.Text>
      </Pill>
    );
  }
  if (status === "running") {
    return (
      <Pill size="sm" variant="orange">
        <Pill.Icon icon={faCircleNotch} spin />
        <Pill.Text>Running</Pill.Text>
      </Pill>
    );
  }
  // paused
  return (
    <Tooltip
      body="Tool is awaiting response. This may take a while."
      placement="left"
      asChild
    >
      <div>
        <Pill size="sm" variant="yellow">
          <Pill.Icon icon={faCircleNotch} spin />
          <Pill.Text>Awaiting response</Pill.Text>
        </Pill>
      </div>
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// CollapsiblePre
// ---------------------------------------------------------------------------

type ToolCallContentWindowData = {
  title: string;
  content: string;
};

const COLLAPSED_MAX_HEIGHT = 200;

export const CollapsiblePre: React.FC<{
  children: string;
  title: string;
}> = ({ children, title }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const preRef = useRef<HTMLPreElement>(null);
  const { open: openWindow } = useFloatingWindowsActions();

  useEffect(() => {
    if (!preRef.current) return;

    const observer = new ResizeObserver(() => {
      if (!preRef.current || !preRef.current.checkVisibility()) return;
      setNeedsExpansion(preRef.current.scrollHeight > COLLAPSED_MAX_HEIGHT);
    });
    observer.observe(preRef.current);
    return () => observer.disconnect();
  }, []);

  const handleOpenFullscreen = () => {
    openWindow(
      {
        id: `tool-call-content-${uuidv4()}`,
        type: "tool-call-content",
        target: undefined,
      },
      { content: children, title } satisfies ToolCallContentWindowData,
    );
  };

  return (
    <div className="group/collapsible relative mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="absolute top-2 right-2 z-10 flex gap-1.5 opacity-0 transition-opacity group-hover/collapsible:opacity-100">
        <CopyTextIcon
          feedback="inline-only-checkmark"
          size="xs"
          tooltip="Copy"
          value={children}
        />
        <Tooltip placement="top" title="Expand" asChild>
          <IconButton
            icon={faExpand}
            size="xs"
            onClick={handleOpenFullscreen}
          />
        </Tooltip>
      </div>
      <pre
        ref={preRef}
        className={twJoin(
          "overflow-hidden whitespace-pre-wrap break-all font-mono text-gray-700 text-xs",
        )}
        style={{
          maxHeight: isExpanded ? undefined : COLLAPSED_MAX_HEIGHT,
        }}
      >
        {children}
      </pre>
      {needsExpansion && (
        <button
          className="mt-1 font-inter-medium-12px text-indigo-500 hover:text-indigo-600"
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "See less" : "See more"}
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// ToolCallBody
// ---------------------------------------------------------------------------

export const ToolCallBody: React.FC<{
  toolCall: ToolCallContentBlock;
  toolResult: ToolMessage | null;
  showDescription?: boolean;
}> = ({ toolCall, toolResult, showDescription = true }) => {
  const { orgId, wsId } = useParamsDecode<{ orgId: string; wsId: string }>();
  const isError = toolResult?.status === "error";

  const isReadFile =
    toolCall.metadata.type === "read_file" &&
    toolResult?.artifact?.file_id &&
    toolResult?.artifact?.file_env;

  const decisionUrl = getFlowToolDecisionUrl(toolCall, toolResult, orgId, wsId);

  const tabs = [
    {
      key: "input",
      label: "Input",
      content: (
        <CollapsiblePre
          title={`"${toolCall.metadata.display_name}" tool input`}
        >
          {JSON.stringify(toolCall.args, null, 2)}
        </CollapsiblePre>
      ),
    },
    {
      key: "output",
      label: "Output",
      disabled: !toolResult,
      content: toolResult ? (
        isReadFile ? (
          <div className="mt-3">
            <FileCard
              fileId={toolResult.artifact!.file_id}
              fileEnv={toolResult.artifact!.file_env}
              floatingWindowPlacement="left"
              floatingWindowOffset={[0, 600]}
            />
          </div>
        ) : (
          <CollapsiblePre
            title={`"${toolCall.metadata.display_name}" tool output`}
          >
            {formatToolOutput(toolResult.content)}
          </CollapsiblePre>
        )
      ) : null,
    },
  ];

  return (
    <div className="flex flex-col gap-3 px-3 pb-3">
      <Divider />
      {showDescription && toolCall.metadata.description && (
        <div>
          <span className="font-inter-medium-11px text-gray-500">
            Description
          </span>
          <p className="line-clamp-3 font-inter-normal-13px text-gray-800">
            {toolCall.metadata.description}
          </p>
        </div>
      )}
      {isError && (
        <Callout variant="warning">
          <span className="font-inter-normal-12px">
            {formatToolOutput(toolResult.content)}
          </span>
        </Callout>
      )}
      {toolResult?.artifact?.user_hint && (
        <Callout variant="info">
          <span className="font-inter-normal-12px">
            {String(toolResult.artifact.user_hint)}
          </span>
        </Callout>
      )}
      <Tabs
        customButton={
          decisionUrl ? (
            <button
              className="flex items-center gap-1 font-inter-medium-12px text-indigo-600 hover:underline"
              data-loc="tool-call-view-decision"
              type="button"
              onClick={() =>
                window.open(window.location.origin + decisionUrl, "_blank")
              }
            >
              View decision
              <Icon
                icon={faArrowUpRightFromSquare}
                size="2xs"
                variant="active"
              />
            </button>
          ) : undefined
        }
        defaultActiveKey="input"
        tabClassName="px-2"
        tabListClassName="items-center border-b border-gray-200"
        tabs={tabs}
        unmountPanels={false}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// ToolCallAccordionItem
// ---------------------------------------------------------------------------

export const ToolCallAccordionItem: React.FC<{
  toolCall: ToolCallContentBlock;
  toolResult: ToolMessage | null;
  status: ToolCallStatus | null;
  showTokenUsage?: boolean;
  showDescription?: boolean;
}> = ({
  toolCall,
  toolResult,
  status,
  showTokenUsage = true,
  showDescription = true,
}) => {
  const durationMs = toolResult
    ? new Date(toolResult.finished_at).getTime() -
      new Date(toolResult.started_at).getTime()
    : null;

  return (
    <AccordionDisclosure
      erroredBorder={status === "error"}
      icon={<ToolTypeIcon type={toolCall.metadata.type} />}
      rightSlot={
        <div className="flex items-center gap-1.5">
          {toolResult && durationMs != null && (
            <Tooltip placement="top" title="Execution duration">
              <DurationPill durationMs={durationMs} />
            </Tooltip>
          )}
          {showTokenUsage && toolResult && toolResult.token_count != null && (
            <Tooltip placement="top" title="Token usage">
              <TokensPill count={toolResult.token_count} />
            </Tooltip>
          )}
          {status && <ToolCallStatusPill status={status} />}
        </div>
      }
      title={toolCall.metadata.display_name}
    >
      <ToolCallBody
        showDescription={showDescription}
        toolCall={toolCall}
        toolResult={toolResult}
      />
    </AccordionDisclosure>
  );
};
